import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth, db } from '../firebase';
import {collection, query, where, getDocs, limit} from 'firebase/firestore';
import { getFavoriteCafeIds, toggleFavoriteCafe } from '../utils/favoriteCafes';
import { getUserRole } from '../utils/getUserRole';
import {
  cancelReservation,
  isReservationActive,
  listReservationsByIsbn,
  reserveBookInCafe,
} from '../utils/reservations';
import {
  getBookReviewStats,
  getBookRatingDistribution,
  getOwnReview,
  listBookReviews,
  reportReview,
  setReviewHidden,
  deleteOwnReview,
  upsertReview,
  voteReview,
} from '../utils/reviews';
import { REVIEW_VOTE } from '../utils/reviewUtils';

const renderStars = (rating = 0) => {
  const r = Math.max(0, Math.min(5, Number(rating) || 0));
  return `${'*'.repeat(r)}${'-'.repeat(5 - r)}`;
};

const renderStarIcons = (rating = 0) => {
  const r = Math.max(0, Math.min(5, Number(rating) || 0));
  return r > 0 ? '\u2B50'.repeat(r) : '\u2014';
};

const formatReviewCount = (count) => {
  const n = Number(count || 0);
  const mod10 = n % 10;
  const mod100 = n % 100;
  let word = '\u0432\u0456\u0434\u0433\u0443\u043a\u0456\u0432';
  if (mod10 === 1 && mod100 !== 11) word = '\u0432\u0456\u0434\u0433\u0443\u043a';
  else if ([2, 3, 4].includes(mod10) && ![12, 13, 14].includes(mod100)) word = '\u0432\u0456\u0434\u0433\u0443\u043a\u0438';
  return `${n} ${word}`;
};

const getInitials = (name = '') => {
  const raw = String(name || '').trim();
  if (!raw) return '\u041A';
  const parts = raw.split(/\s+/).filter(Boolean);
  return `${parts[0]?.[0] || ''}${parts[1]?.[0] || ''}`.toUpperCase();
};

const ratingLabelByValue = {
  5: '\u0414\u0443\u0436\u0435 \u0441\u043F\u043E\u0434\u043E\u0431\u0430\u043B\u0430\u0441\u044C',
  4: '\u0421\u043F\u043E\u0434\u043E\u0431\u0430\u043B\u0430\u0441\u044C',
  3: '\u041D\u043E\u0440\u043C',
  2: '\u041D\u0435 \u0441\u043F\u043E\u0434\u043E\u0431\u0430\u043B\u0430\u0441\u044C',
  1: '\u0417\u0440\u044F \u0432\u0438\u0442\u0440\u0430\u0447\u0435\u043D\u0438\u0439 \u0447\u0430\u0441',
};

const chunk = (arr, size = 10) => {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};

function RatingStars({ value, size = 16 }) {
  const num = Math.max(0, Math.min(5, Number(value) || 0));
  const fillPercent = `${(num / 5) * 100}%`;
  const starText = '\u2605\u2605\u2605\u2605\u2605';
  return (
    <span style={{ position: 'relative', display: 'inline-block', lineHeight: 1, fontSize: size }}>
      <span style={{ color: '#d1d5db' }}>{starText}</span>
      <span
        style={{
          position: 'absolute',
          inset: 0,
          width: fillPercent,
          overflow: 'hidden',
          color: '#f59e0b',
          whiteSpace: 'nowrap',
        }}
      >
        {starText}
      </span>
    </span>
  );
}

function MapButton({ onClick, variant = 'soft' }) {
  const [isHover, setIsHover] = useState(false);
  const isAccent = variant === 'accent';
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
      style={{
        padding: isAccent ? '8px 12px' : '7px 11px',
        borderRadius: 10,
        border: isAccent ? '1px solid #0b5226' : '1px solid #cbd5e1',
        background: isAccent
          ? (isHover ? '#0a4721' : '#0b5226')
          : (isHover ? '#f8fafc' : '#fff'),
        color: isAccent ? '#fff' : '#334155',
        fontWeight: isAccent ? 700 : 600,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        transition: 'background-color 140ms ease',
      }}
    >
      {'\u041F\u043E\u043A\u0430\u0437\u0430\u0442\u0438 \u043D\u0430 \u043C\u0430\u043F\u0456'}
    </button>
  );
}

export default function BookPage() {
  const { isbn } = useParams();
  const navigate = useNavigate();
  const [user] = useAuthState(auth);

  const [tpl, setTpl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cafes, setCafes] = useState([]); // [{name, address, count}]
  const [favoriteCafeIds, setFavoriteCafeIds] = useState([]);
  const [favoriteBusyId, setFavoriteBusyId] = useState(null);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth < 900 : false);
  const [availableCopies, setAvailableCopies] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [reservationBusyCafe, setReservationBusyCafe] = useState(null);
  const [reservationModalOpen, setReservationModalOpen] = useState(false);
  const [reservationError, setReservationError] = useState('');
  const [reservationSuccess, setReservationSuccess] = useState('');
  const [reserveBtnHover, setReserveBtnHover] = useState(false);
  const [timeNow, setTimeNow] = useState(Date.now());
  const [reviewStats, setReviewStats] = useState(null);
  const [ratingDistribution, setRatingDistribution] = useState({ 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 });
  const [ratingHintOpen, setRatingHintOpen] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [reviewFilter, setReviewFilter] = useState('all');
  const [reviewSort, setReviewSort] = useState('newest');
  const [myReview, setMyReview] = useState(null);
  const [reviewDraft, setReviewDraft] = useState({ rating: 5, reviewText: '' });
  const [reviewHoverRating, setReviewHoverRating] = useState(0);
  const [reviewFormOpen, setReviewFormOpen] = useState(false);
  const [reviewBusy, setReviewBusy] = useState(false);
  const [reviewError, setReviewError] = useState('');
  const [reviewSuccess, setReviewSuccess] = useState('');
  const [expandedReviewIds, setExpandedReviewIds] = useState({});
  const [scrollToOwnReview, setScrollToOwnReview] = useState(false);
  const [deleteReviewModalOpen, setDeleteReviewModalOpen] = useState(false);
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [reportTargetReviewId, setReportTargetReviewId] = useState(null);
  const [reportReason, setReportReason] = useState('');
  const [reportBusy, setReportBusy] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isBarista, setIsBarista] = useState(false);
  const [isDescExpanded, setIsDescExpanded] = useState(false);
  const [canExpandDesc, setCanExpandDesc] = useState(false);
  const descRef = useRef(null);

  // слухаємо ресайз для адаптиву
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 900);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    setIsDescExpanded(false);
  }, [isbn]);

  useEffect(() => {
    if (isDescExpanded) return;
    const el = descRef.current;
    if (!el) {
      setCanExpandDesc(false);
      return;
    }
    setCanExpandDesc(el.scrollHeight - el.clientHeight > 1);
  }, [tpl, isMobile, isDescExpanded]);

  useEffect(() => {
    if (!isbn) return;

    (async () => {
      try {
        // 1) Шаблон за ISBN
        const tmplSnap = await getDocs(
          query(collection(db, 'bookTemplates'), where('isbn', '==', isbn), limit(1))
        );
        const tmpl = tmplSnap.empty
          ? null
          : { id: tmplSnap.docs[0].id, __col: 'bookTemplates', ...tmplSnap.docs[0].data() };

        // 2) Один екземпляр книги за ISBN (щоб взяти ціни/локації/owner тощо)
        const oneBookSnap = await getDocs(
          query(collection(db, 'books'), where('isbn', '==', isbn), limit(1))
        );
        const oneBook = oneBookSnap.empty
          ? null
          : { id: oneBookSnap.docs[0].id, __col: 'books', ...oneBookSnap.docs[0].data() };

        // діагностика
        console.log('tmpl (bookTemplates):', tmpl);
        console.log('tmpl.meta:', {
          description: tmpl?.description,
          category: tmpl?.category,
          subcategory: tmpl?.subcategory
        });
        console.log('oneBook (books):', oneBook);


        // 3) Усі доступні екземпляри — рахуємо "Вільно" і групуємо кав’ярні
        const availableSnap = await getDocs(
          query(collection(db, 'books'), where('isbn', '==', isbn), where('status', '==', 'available'))
        );
        const availableRows = availableSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        const grouped = new Map();
        availableRows.forEach((row) => {
          const { cafeName = 'Невідомо', cafeAddress = '' } = row;
          const key = `${cafeName}|||${cafeAddress}`;
          grouped.set(key, (grouped.get(key) || 0) + 1);
        });

        // 3a) довідник тільки потрібних кав'ярень (без читання всієї колекції cafes)
        const cafesDict = new Map();
        const uniqueCafeNames = Array.from(
          new Set(availableRows.map((row) => String(row.cafeName || '').trim()).filter(Boolean)),
        );
        for (const pack of chunk(uniqueCafeNames, 10)) {
          const cafesSnap = await getDocs(
            query(collection(db, 'cafes'), where('cafeName', 'in', pack)),
          );
          cafesSnap.forEach((docu) => {
            const x = docu.data();
            cafesDict.set(x.cafeName, {
              id: docu.id,
              address: x.cafeAddress || '',
              lat: typeof x.lat === 'number' ? x.lat : null,
              lng: typeof x.lng === 'number' ? x.lng : null,
              mapsLink: x.mapsLink || '',
            });
          });
        }

        // 3b) масив для відображення
        const cafesArr = Array.from(grouped.entries()).map(([key, count]) => {
          const [name, addressFromBook] = key.split('|||');
          const meta = cafesDict.get(name) || {};
          return {
            id: meta.id || null,
            name,
            count,
            address: meta.address || addressFromBook,
            mapsLink: meta.mapsLink || '',
            lat: meta.lat ?? null,
            lng: meta.lng ?? null,
          };
        });

        // 4) МЕРДЖ ДАНИХ
// База — екземпляр (якщо є), інакше шаблон
let merged = oneBook || tmpl || null;

if (merged && tmpl) {
  // Нормалізуємо рядки (щоб пусті/пробільні не «перекривали» нормальні значення)
  const norm = (v) => (typeof v === 'string' ? v.trim() : v);

  merged = {
    // 4.1. Спершу беремо «живі» дані з екземпляра (ціни, локації, статус)
    ...oneBook,
    // 4.2. Далі — базові метадані з шаблону (назва/автор/рік/обкладинка/видавництво/сторінки)
    title:       norm(oneBook?.title)       || tmpl.title,
    author:      norm(oneBook?.author)      || tmpl.author,
    year:        norm(oneBook?.year)        || tmpl.year,
    coverUrl:    norm(oneBook?.coverUrl)    || tmpl.coverUrl,
    pages:       norm(oneBook?.pages)       || tmpl.pages,
    publisher:   norm(oneBook?.publisher)   || tmpl.publisher,
    // 4.3. ВАЖЛИВО: метадані каталогу завжди беремо з шаблону, якщо там є значення
    description: norm(tmpl.description)     || norm(oneBook?.description) || '',
    category:    norm(tmpl.category)        || norm(oneBook?.category)    || '',
    subcategory: norm(tmpl.subcategory)     || norm(oneBook?.subcategory) || '',
    // службові
    __col: oneBook ? 'books' : 'bookTemplates',
    id: oneBook?.id || tmpl.id,
    isbn, // щоб не загубити
  };
} else if (tmpl) {
  // є лише шаблон — просто фіксуємо колекцію
  merged = { __col: 'bookTemplates', ...tmpl };
}


        // 5) проставляємо кількість доступних екземплярів
        if (merged) merged.availableCount = availableSnap.size;

        setTpl(merged);
        setCafes(cafesArr);
        setAvailableCopies(availableRows);
      } finally {
        setLoading(false);
      }
    })();
  }, [isbn]);

  useEffect(() => {
    (async () => {
      if (!user?.uid) {
        setFavoriteCafeIds([]);
        return;
      }
      try {
        const ids = await getFavoriteCafeIds(user.uid);
        setFavoriteCafeIds(ids);
      } catch {
        setFavoriteCafeIds([]);
      }
    })();
  }, [user?.uid]);

  const onToggleFavoriteCafe = async (cafeId) => {
    if (!user?.uid || !cafeId || favoriteBusyId) return;
    setFavoriteBusyId(cafeId);
    try {
      const res = await toggleFavoriteCafe(user.uid, cafeId, user.email || '');
      setFavoriteCafeIds(res.ids);
    } finally {
      setFavoriteBusyId(null);
    }
  };

  useEffect(() => {
    const id = setInterval(() => setTimeNow(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);

  const activeReservations = useMemo(
    () => (reservations || []).filter((r) => isReservationActive(r, timeNow)),
    [reservations, timeNow],
  );

  const reservationByBookId = useMemo(() => {
    const m = new Map();
    activeReservations.forEach((r) => {
      if (r?.bookId) m.set(String(r.bookId), r);
    });
    return m;
  }, [activeReservations]);

  const freeCountsByCafe = useMemo(() => {
    const m = new Map();
    availableCopies.forEach((b) => {
      const cafeName = b.cafeName || 'Невідомо';
      if (reservationByBookId.has(String(b.id))) return;
      m.set(cafeName, (m.get(cafeName) || 0) + 1);
    });
    return m;
  }, [availableCopies, reservationByBookId]);

  const userActiveReservation = useMemo(() => {
    if (!user?.uid) return null;
    return activeReservations.find((r) => r.userUid === user.uid) || null;
  }, [activeReservations, user?.uid]);

  const availableBookIds = useMemo(
    () => new Set((availableCopies || []).map((b) => String(b.id))),
    [availableCopies],
  );

  const effectiveUserReservation = useMemo(() => {
    if (!userActiveReservation?.bookId) return null;
    return availableBookIds.has(String(userActiveReservation.bookId)) ? userActiveReservation : null;
  }, [availableBookIds, userActiveReservation]);

  const totalFreeCopies = useMemo(() => {
    let total = 0;
    freeCountsByCafe.forEach((v) => {
      total += Number(v) || 0;
    });
    return total;
  }, [freeCountsByCafe]);

  const hasFreeCopies = totalFreeCopies > 0;
  const hasOwnActiveReservation = Boolean(effectiveUserReservation);
  const visibleCafesWithFreeCopies = useMemo(
    () => cafes.filter((c) => (freeCountsByCafe.get(c.name) ?? 0) > 0),
    [cafes, freeCountsByCafe],
  );
  const hasSingleAvailableCafe = !hasOwnActiveReservation && visibleCafesWithFreeCopies.length === 1;
  const hasMultipleAvailableCafes = !hasOwnActiveReservation && visibleCafesWithFreeCopies.length >= 2;
  const singleAvailableCafe = hasSingleAvailableCafe ? visibleCafesWithFreeCopies[0] : null;

  const reservedCafe = useMemo(() => {
    if (!effectiveUserReservation) return null;
    const fallbackName = effectiveUserReservation.cafeName || 'Кавʼярня';
    const fromList = cafes.find((c) => c.name === fallbackName);
    return {
      id: fromList?.id || null,
      name: fromList?.name || fallbackName,
      address: fromList?.address || effectiveUserReservation.cafeAddress || '',
      mapsLink: fromList?.mapsLink || '',
      lat: fromList?.lat ?? null,
      lng: fromList?.lng ?? null,
    };
  }, [cafes, effectiveUserReservation]);

  const openCafeOnMap = (cafe) => {
    if (!cafe) return;
    const direct = (cafe.mapsLink || '').trim();
    let url = direct;
    if (!url) {
      const query =
        cafe.lat != null && cafe.lng != null
          ? `${cafe.lat},${cafe.lng}`
          : `${cafe.name} ${cafe.address || ''}`;
      url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const loadReservations = useCallback(async () => {
    if (!isbn) {
      setReservations([]);
      return;
    }
    try {
      const rows = await listReservationsByIsbn(isbn);
      setReservations(rows || []);
      setReservationError('');
    } catch (e) {
      setReservationError(e?.message || 'Не вдалося завантажити бронювання.');
    }
  }, [isbn]);

  useEffect(() => {
    loadReservations();
  }, [loadReservations]);

  const formatUntil = (ms) =>
    new Date(ms).toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit' });

  const formatRemaining = (expiresAtMs) => {
    const leftMs = Number(expiresAtMs) - timeNow;
    if (!Number.isFinite(leftMs) || leftMs <= 0) return 'залишилось 0 хв';
    const totalMinutes = Math.floor(leftMs / 60000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (hours <= 0) return `залишилось ${minutes} хв`;
    return `залишилось ${hours} год ${minutes} хв`;
  };

  const onReserveAtCafe = async (cafeName) => {
    if (isBarista) {
      setReservationError('Бариста не може бронювати книги.');
      return;
    }
    if (!user?.uid) {
      navigate('/login');
      return;
    }

    const candidates = availableCopies.filter(
      (b) => (b.cafeName || 'Невідомо') === cafeName && !reservationByBookId.has(String(b.id)),
    );
    if (candidates.length === 0) {
      setReservationError('Немає вільних примірників у вибраній кавʼярні.');
      return;
    }

    setReservationBusyCafe(cafeName);
    setReservationError('');
    setReservationSuccess('');
    try {
      await reserveBookInCafe({
        user,
        isbn,
        cafeName,
        candidateBooks: candidates,
      });
      await loadReservations();
      setReservationSuccess('');
      setReservationModalOpen(false);
    } catch (e) {
      setReservationError(e?.message || 'Не вдалося забронювати книгу.');
    } finally {
      setReservationBusyCafe(null);
    }
  };

  const onCancelMyReservation = async () => {
    if (isBarista) {
      setReservationError('Бариста не може керувати бронюванням як користувач.');
      return;
    }
    if (!user?.uid || !effectiveUserReservation?.bookId) return;
    setReservationError('');
    setReservationSuccess('');
    try {
      await cancelReservation({ userUid: user.uid, bookId: effectiveUserReservation.bookId });
      await loadReservations();
      setReservationSuccess('Бронювання скасовано.');
    } catch (e) {
      setReservationError(e?.message || 'Не вдалося скасувати бронювання.');
    }
  };  

  const reviewBookId = useMemo(() => {
    if (!isbn) return null;
    if (tpl?.templateId) return String(tpl.templateId);
    if (tpl?.__col === 'bookTemplates' && tpl?.id) return String(tpl.id);
    return `isbn:${isbn}`;
  }, [isbn, tpl?.templateId, tpl?.__col, tpl?.id]);

  const reloadReviews = useCallback(async () => {
    if (!reviewBookId) return;
    setReviewsLoading(true);
    setReviewError('');
    try {
      const [stats, distribution, publicReviews, ownReview] = await Promise.all([
        getBookReviewStats(reviewBookId),
        getBookRatingDistribution(reviewBookId),
        listBookReviews({
          bookId: reviewBookId,
          currentUserId: user?.uid || null,
          verifiedOnly: reviewFilter === 'verified',
          sort: reviewSort === 'helpful' ? 'helpful' : 'newest',
        }),
        user?.uid ? getOwnReview({ bookId: reviewBookId, userId: user.uid }) : Promise.resolve(null),
      ]);

      setReviewStats(stats || null);
      setRatingDistribution(distribution || { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 });
      setReviews(publicReviews || []);
      setMyReview(ownReview || null);

      if (ownReview && !reviewFormOpen) {
        setReviewDraft({
          rating: Number(ownReview.rating || 5),
          reviewText: ownReview.reviewText || '',
        });
      }
    } catch (e) {
      setReviewError(e?.message || 'Не вдалося завантажити відгуки.');
    } finally {
      setReviewsLoading(false);
    }
  }, [reviewBookId, reviewFilter, reviewSort, user?.uid, reviewFormOpen]);

  useEffect(() => {
    if (!user?.uid) {
      setIsAdmin(false);
      setIsBarista(false);
      return;
    }
    (async () => {
      const role = await getUserRole(user.uid);
      setIsAdmin(role === 'admin');
      setIsBarista(role === 'barista');
    })();
  }, [user?.uid]);

  useEffect(() => {
    if (!isBarista) return;
    setReviewFormOpen(false);
    setReservationModalOpen(false);
  }, [isBarista]);

  useEffect(() => {
    reloadReviews();
  }, [reloadReviews]);

  const onSubmitReview = async (e) => {
    e?.preventDefault?.();
    if (isBarista) {
      setReviewError('Бариста не може ставити оцінки або залишати відгуки.');
      return;
    }
    if (!user?.uid) {
      navigate('/login');
      return;
    }
    if (!reviewBookId) return;
    if (Number(reviewDraft.rating) < 1 || Number(reviewDraft.rating) > 5) {
      setReviewError('\u041E\u0431\u0435\u0440\u0456\u0442\u044C \u043E\u0446\u0456\u043D\u043A\u0443 \u0432\u0456\u0434 1 \u0434\u043E 5.');
      return;
    }

    setReviewBusy(true);
    setReviewError('');
    setReviewSuccess('');
    try {
      await upsertReview({
        user,
        bookId: reviewBookId,
        isbn,
        templateId: tpl?.templateId || (tpl?.__col === 'bookTemplates' ? tpl?.id : null),
        rating: Number(reviewDraft.rating),
        reviewText: reviewDraft.reviewText,
      });
      setReviewFormOpen(false);
      await reloadReviews();
      setReviewSuccess('Відгук збережено');
      setScrollToOwnReview(true);
    } catch (e) {
      setReviewError(e?.message || 'Не вдалося зберегти відгук.');
    } finally {
      setReviewBusy(false);
    }
  };

  const onVoteReview = async (reviewId, voteType) => {
    if (!user?.uid) {
      navigate('/login');
      return;
    }
    try {
      await voteReview({ reviewId, userId: user.uid, voteType });
      await reloadReviews();
    } catch (e) {
      setReviewError(e?.message || 'Не вдалося проголосувати.');
    }
  };

  const onToggleReviewExpanded = (reviewId) => {
    setExpandedReviewIds((prev) => ({ ...prev, [reviewId]: !prev[reviewId] }));
  };

  const onDeleteMyReview = async () => {
    if (isBarista) return;
    if (!user?.uid || !reviewBookId) return;
    setDeleteReviewModalOpen(true);
  };

  const confirmDeleteMyReview = async () => {
    if (isBarista) return;
    if (!user?.uid || !reviewBookId) return;
    setReviewBusy(true);
    setReviewError('');
    setReviewSuccess('');
    try {
      await deleteOwnReview({ user, bookId: reviewBookId });
      setReviewFormOpen(false);
      setDeleteReviewModalOpen(false);
      setReviewDraft({ rating: 0, reviewText: '' });
      await reloadReviews();
      setReviewSuccess('\u0412\u0456\u0434\u0433\u0443\u043A \u0432\u0438\u0434\u0430\u043B\u0435\u043D\u043E');
    } catch (e) {
      setReviewError(e?.message || '\u041D\u0435 \u0432\u0434\u0430\u043B\u043E\u0441\u044F \u0432\u0438\u0434\u0430\u043B\u0438\u0442\u0438 \u0432\u0456\u0434\u0433\u0443\u043A.');
    } finally {
      setReviewBusy(false);
    }
  };

  useEffect(() => {
    if (!scrollToOwnReview || !myReview?.id) return;
    const el = document.getElementById(`review-${myReview.id}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setScrollToOwnReview(false);
    }
  }, [scrollToOwnReview, myReview?.id, reviews]);

  useEffect(() => {
    if (!reviewSuccess) return;
    const t = setTimeout(() => setReviewSuccess(''), 2200);
    return () => clearTimeout(t);
  }, [reviewSuccess]);

  const onReportReview = async (reviewId) => {
    if (!user?.uid) {
      navigate('/login');
      return;
    }
    setReportTargetReviewId(reviewId);
    setReportReason('');
    setReportModalOpen(true);
  };

  const submitReportReview = async () => {
    if (!user?.uid || !reportTargetReviewId) return;
    setReportBusy(true);
    setReviewError('');
    try {
      await reportReview({ reviewId: reportTargetReviewId, user, reason: reportReason });
      setReportModalOpen(false);
      setReportTargetReviewId(null);
      setReportReason('');
      setReviewSuccess('\u0421\u043A\u0430\u0440\u0433\u0443 \u043D\u0430\u0434\u0456\u0441\u043B\u0430\u043D\u043E');
    } catch (e) {
      setReviewError(e?.message || '\u041D\u0435 \u0432\u0434\u0430\u043B\u043E\u0441\u044F \u043D\u0430\u0434\u0456\u0441\u043B\u0430\u0442\u0438 \u0441\u043A\u0430\u0440\u0433\u0443.');
    } finally {
      setReportBusy(false);
    }
  };

  const onToggleHidden = async (review) => {
    if (!user?.uid || !isAdmin) return;
    try {
      await setReviewHidden({
        reviewId: review.id,
        hidden: review.status !== 'hidden',
        actorUid: user.uid,
      });
      await reloadReviews();
    } catch (e) {
      setReviewError(e?.message || 'Не вдалося змінити статус review.');
    }
  };
  if (loading) return <div style={{ padding: 24 }}>Завантаження…</div>;
  if (!tpl) {
    return (
      <div style={{ padding: 24 }}>
        <h2>Книгу не знайдено</h2>
        <button onClick={() => navigate(-1)}>Назад</button>
      </div>
    );
  }

const {
    title, author, year, coverUrl,
    borrowCount = 0, pages, publisher,
    price,            // ← додали
    borrowPrice,      // ← додали
    } = tpl;
// Людські назви категорій
const mapCategory = (raw) => {
  const v = (raw || '').toString().trim().toLowerCase();
  switch (v) {
    case 'fiction':
      return 'Історії, що захоплюють';
    case 'non-fiction':
    case 'nonfiction':
      return 'Дізнатись нове';
    case 'kids':
    case 'children':
    case 'child':
    case 'kids & teens':
      return 'Для дітей';
    default:
      return raw || '—';
  }
};

// Обчислимо відображувану категорію
const displayCategory = mapCategory(tpl.category);

// нормалізуємо поля: підтримуємо альтернативні назви і прибираємо зайві пробіли
const desc = (tpl.description ?? tpl.desc ?? tpl.about ?? '').trim();
const sub  = (tpl.subcategory ?? tpl.subCategory ?? tpl.subgenre ?? '').trim();

const displayPrice = (borrowPrice ?? price); // показуємо спершу borrowPrice, інакше price

  const reservedByYouBlock = hasOwnActiveReservation && !isBarista ? (
    <div style={{ marginTop: 14, width: '100%', borderTop: '1px solid #e5e7eb', paddingTop: 12 }}>
      <div style={{ fontWeight: 800, marginBottom: 10, fontSize: 15 }}>
        {'\u0412\u0438 \u0437\u0430\u0431\u0440\u043E\u043D\u044E\u0432\u0430\u043B\u0438 \u0442\u0443\u0442'}
      </div>
      <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: 10, background: '#fafafa' }}>
        <div style={{ fontWeight: 700 }}>
          {reservedCafe?.name || effectiveUserReservation?.cafeName || '\u041A\u0430\u0432\u02BC\u044F\u0440\u043D\u044F'}
        </div>
        {(reservedCafe?.address || effectiveUserReservation?.cafeAddress) && (
          <div style={{ color: '#6b7280', fontSize: 13 }}>
            {reservedCafe?.address || effectiveUserReservation?.cafeAddress}
          </div>
        )}
        <div style={{ marginTop: 8, fontSize: 13, color: '#14532d' }}>
          {'\u2705'} {'\u0417\u0430\u0431\u0440\u043E\u043D\u044C\u043E\u0432\u0430\u043D\u043E \u0434\u043E'} {formatUntil(Number(effectiveUserReservation?.expiresAtMs || 0))} ({formatRemaining(Number(effectiveUserReservation?.expiresAtMs || 0))})
        </div>
        <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <MapButton onClick={() => openCafeOnMap(reservedCafe || effectiveUserReservation)} variant="accent" />
          <button
            type="button"
            onClick={onCancelMyReservation}
            style={{
              padding: '8px 12px',
              borderRadius: 10,
              border: '1px solid #d1d5db',
              background: '#fff',
              color: '#111827',
              fontWeight: 600,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            {'\u0421\u043A\u0430\u0441\u0443\u0432\u0430\u0442\u0438 \u0431\u0440\u043E\u043D\u044E\u0432\u0430\u043D\u043D\u044F'}
          </button>
        </div>
      </div>
    </div>
  ) : null;


  const gridStyle = isMobile
    ? { display: 'grid', gridTemplateColumns: '1fr', gap: 16 }
    : { display: 'grid', gridTemplateColumns: '260px 1fr 320px', gap: 24 };
  const heroStyle = {
    background: '#fff',
    border: '1px solid #e6ebf1',
    borderRadius: 14,
    padding: isMobile ? 12 : 16,
    boxShadow: '0 1px 3px rgba(15, 23, 42, 0.04)',
  };

  return (
    <div style={{ background: '#F6F4EF', minHeight: '100vh' }}>
      <div style={{ maxWidth: 1100, margin: '24px auto', padding: '0 16px' }}>
        {/* Верхній блок: обкладинка + назва/опис + статус */}
        <div style={heroStyle}>
          <div style={gridStyle}>
          {/* обкладинка */}
          <div style={{
            padding: 8,
            display: 'flex',
            flexDirection: 'column',         // ← щоб ціна стала під обкладинкою
            alignItems: 'center',
            justifyContent: 'flex-start'
            }}>
            <img
                src={coverUrl || 'https://placehold.co/220x320?text=No+cover'}
                alt={title}
                style={{ maxWidth: '100%', maxHeight: isMobile ? 368 : 414, objectFit: 'contain' }}
            />

            {/* Блок з цінами: показуємо лише якщо є хоча б одне значення */}
            {displayPrice != null && (
                <div
                    style={{
                    marginTop: 12,
                    width: '100%',
                    borderTop: '1px solid #e5e7eb',
                    paddingTop: 12,
                    textAlign: 'center',
                    }}
                >
                    <div
                      title="Зверніться до баристи, щоб взяти книгу. І не забудьте принести свою, щоб поповнити баланс."
                      style={{ fontSize: isMobile ? 20 : 22, fontWeight: 500, color: '#111827', cursor: 'help' }}
                    >
                    {displayPrice} монет
                    </div>
                </div>
                )}

            <div style={{ marginTop: 12, width: '100%' }}>
              {!isBarista && (
                <button
                  type="button"
                  onClick={() => {
                    if (hasOwnActiveReservation || !hasFreeCopies) return;
                    setReservationModalOpen(true);
                  }}
                  onMouseEnter={() => setReserveBtnHover(true)}
                  onMouseLeave={() => setReserveBtnHover(false)}
                  disabled={hasOwnActiveReservation || !hasFreeCopies}
                  style={{
                    width: '100%',
                    height: 38,
                    borderRadius: 10,
                    border: `1px solid ${hasOwnActiveReservation || !hasFreeCopies ? '#9ca3af' : '#0b5226'}`,
                    background: hasOwnActiveReservation || !hasFreeCopies
                      ? '#e5e7eb'
                      : reserveBtnHover
                        ? '#0a4721'
                        : '#0b5226',
                    color: hasOwnActiveReservation || !hasFreeCopies ? '#374151' : '#fff',
                    fontWeight: 700,
                    cursor: hasOwnActiveReservation || !hasFreeCopies ? 'default' : 'pointer',
                    transition: 'background-color 140ms ease',
                  }}
                >
                  {hasOwnActiveReservation ? 'Заброньовано' : !hasFreeCopies ? 'Немає в наявності' : 'Забронювати на 6 годин'}
                </button>
              )}
              {reservationSuccess && !hasOwnActiveReservation && (
                <div style={{ marginTop: 8, fontSize: 13, color: '#14532d' }}>{reservationSuccess}</div>
              )}
              {reservationError && (
                <div style={{ marginTop: 8, fontSize: 13, color: '#991b1b' }}>{reservationError}</div>
              )}
            </div>

            {isMobile && reservedByYouBlock}
            </div>


          {/* назва + опис (тут же переносимо "Кількість сторінок") */}
          <div style={{ padding: 8 }}>
            <h1 style={{ margin: '0 0 6px 0' }}>{title}</h1>
            <div style={{ marginBottom: 6, color: '#374151', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <RatingStars value={reviewStats?.averageRatingAll ?? 0} size={16} />
              <span>{reviewStats?.averageRatingAll ?? '\u2014'} ({formatReviewCount(reviewStats?.ratingsCountAll ?? 0)})</span>
            </div>
            <div style={{ fontWeight: 700, marginBottom: 12, color: '#374151' }}>
              {author}{year ? ` | ${year}` : ''}
            </div>
            {publisher && <div style={{ color: '#6b7280', marginBottom: 6 }}>Видавництво: {publisher}</div>}
            {pages ? (
              <div style={{ color: '#6b7280', marginBottom: 10 }}>Кількість сторінок: {pages}</div>
            ) : null}
            {!isMobile && reservedByYouBlock}

          </div>

          {/* правий статус-блок (прибрали "Читають") */}
          <aside style={{ padding: 8 }}>
            <div style={{ fontWeight: 800, marginBottom: 12 }}>СТАТУС</div>

            <div style={{ marginBottom: 10 }}>
              <div style={{ fontWeight: 700 }}>Прочитали:</div>
              <div>{borrowCount}</div>
            </div>

            <div style={{ marginBottom: 10 }}>
              <div style={{ fontWeight: 700 }}>Статус наявності:</div>
              {hasOwnActiveReservation ? (
                <div style={{ color: '#92400e', fontWeight: 700 }}>
                  🟡 Заброньовано вами
                  <div style={{ fontSize: 13, fontWeight: 600, marginTop: 2 }}>
                    Залишилось {formatRemaining(Number(effectiveUserReservation?.expiresAtMs || 0)).replace('залишилось ', '')}
                  </div>
                </div>
              ) : (
                <div style={{ color: hasFreeCopies ? '#166534' : '#991b1b', fontWeight: 700 }}>
                  {hasFreeCopies ? '🟢 Доступна' : '🔴 Немає в наявності'}
                </div>
              )}
            </div>

            <div style={{ marginTop: 12, fontWeight: 700 }}>Розділ:</div>
            <div>{displayCategory}</div>
            <div style={{ marginTop: 8, fontWeight: 700 }}>Підрозділ:</div>
            <div>{sub || '—'}</div>

            {hasSingleAvailableCafe && (
              <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid #e5e7eb' }}>
                <div style={{ fontWeight: 700, marginBottom: 8 }}>{'\u0414\u0435 \u0434\u043E\u0441\u0442\u0443\u043F\u043D\u0430'}</div>
                <div>
                  <a href={`/cafe/${singleAvailableCafe?.id}`} style={{ color: '#0b5226', fontWeight: 700, textDecoration: 'none' }}>
                    {singleAvailableCafe?.name}
                  </a>
                  {' \u2014 '}{Number(singleAvailableCafe?.count || 0)} {'\u0448\u0442.'}
                  {singleAvailableCafe?.address && <div style={{ color: '#6b7280', fontSize: 13 }}>{singleAvailableCafe.address}</div>}
                </div>
                <div style={{ marginTop: 8 }}>
                  <MapButton onClick={() => openCafeOnMap(singleAvailableCafe)} />
                </div>
              </div>
            )}


            
          </aside>
          </div>
          {desc && (
            <div style={{ marginTop: 12, padding: '0 8px 8px' }}>
              <div
                ref={descRef}
                style={{
                  lineHeight: 1.6,
                  color: '#111827',
                  whiteSpace: 'pre-line',
                  ...(isDescExpanded
                    ? {}
                    : {
                        display: '-webkit-box',
                        WebkitLineClamp: 4,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }),
                }}
              >
                {desc}
              </div>
              {canExpandDesc && (
                <button
                  type="button"
                  onClick={() => setIsDescExpanded((v) => !v)}
                  style={{
                    marginTop: 8,
                    border: 'none',
                    background: 'transparent',
                    color: '#0b5226',
                    fontWeight: 700,
                    padding: 0,
                    cursor: 'pointer',
                  }}
                >
                  {isDescExpanded ? '\u041F\u043E\u043A\u0430\u0437\u0430\u0442\u0438 \u043C\u0435\u043D\u0448\u0435' : '\u041F\u043E\u043A\u0430\u0437\u0430\u0442\u0438 \u0431\u0456\u043B\u044C\u0448\u0435'}
                </button>
              )}
            </div>
          )}
        </div>

        {hasMultipleAvailableCafes && (
          <div style={{ marginTop: 20, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 14 }}>
            <div style={{ fontWeight: 800, marginBottom: 10 }}>{'\u0414\u0435 \u0434\u043E\u0441\u0442\u0443\u043F\u043D\u0430'}</div>
            <ul style={{ margin: 0, paddingLeft: 18, display: 'grid', gap: 8 }}>
              {visibleCafesWithFreeCopies.map((cafe) => (
                <li key={String(cafe.id || cafe.name || cafe.address)} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <div>
                    <a href={`/cafe/${cafe.id}`} style={{ color: '#0b5226', fontWeight: 700, textDecoration: 'none' }}>
                      {cafe.name}
                    </a>
                    {' \u2014 '}{Number(cafe.count || 0)} {'\u0448\u0442.'}
                    {cafe.address && <div style={{ color: '#6b7280', fontSize: 13 }}>{cafe.address}</div>}
                  </div>
                  <MapButton onClick={() => openCafeOnMap(cafe)} />
                </li>
              ))}
            </ul>
          </div>
        )}

        <div style={{
          marginTop: 20,
          background: '#fff',
          border: '1px solid #edf2f7',
          borderRadius: 12,
          padding: 14,
          boxShadow: '0 1px 3px rgba(15, 23, 42, 0.03)',
          fontSize: 14,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <div>
              <h2 style={{ margin: 0, fontSize: isMobile ? 22 : 30 }}>
                {'\u0412\u0456\u0434\u0433\u0443\u043A\u0438 \u0442\u0430 \u043E\u0446\u0456\u043D\u043A\u0438'}
              </h2>
              <div style={{ marginTop: 6, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <div
                  style={{ color: '#374151', position: 'relative', display: 'inline-block', fontSize: 14 }}
                  onMouseEnter={() => setRatingHintOpen(true)}
                  onMouseLeave={() => setRatingHintOpen(false)}
                >
                  {'\u0417\u0430\u0433\u0430\u043B\u044C\u043D\u0438\u0439 \u0440\u0435\u0439\u0442\u0438\u043D\u0433:'} {reviewStats?.averageRatingAll ?? '\u2014'} ({formatReviewCount(reviewStats?.ratingsCountAll ?? 0)})
                  {ratingHintOpen && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        marginTop: 8,
                        background: '#fff',
                        border: '1px solid #e5e7eb',
                        borderRadius: 10,
                        padding: '8px 10px',
                        minWidth: 150,
                        boxShadow: '0 8px 24px rgba(15, 23, 42, 0.12)',
                        zIndex: 20,
                      }}
                    >
                      {[5, 4, 3, 2, 1].map((n) => (
                        <div key={`dist-${n}`} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 12, lineHeight: 1.6 }}>
                          <span>{`${n} \u2B50`}</span>
                          <span>{Number(ratingDistribution?.[n] || 0)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <select value={reviewFilter} onChange={(e) => setReviewFilter(e.target.value)} style={{ height: 32, borderRadius: 8, border: '1px solid #d1d5db', padding: '0 10px', fontSize: 13 }}>
                    <option value="all">{'\u0412\u0441\u0456'}</option>
                    <option value="verified">{'\u041B\u0438\u0448\u0435 \u043F\u0435\u0440\u0435\u0432\u0456\u0440\u0435\u043D\u0456'}</option>
                  </select>
                  <select value={reviewSort} onChange={(e) => setReviewSort(e.target.value)} style={{ height: 32, borderRadius: 8, border: '1px solid #d1d5db', padding: '0 10px', fontSize: 13 }}>
                    <option value="newest">{'\u041D\u0430\u0439\u043D\u043E\u0432\u0456\u0448\u0456'}</option>
                    <option value="helpful">{'\u041D\u0430\u0439\u043A\u043E\u0440\u0438\u0441\u043D\u0456\u0448\u0456'}</option>
                  </select>
                </div>
              </div>
            </div>
            {user && !myReview && !isBarista && (
              <button
                type="button"
                onClick={() => {
                  setReviewHoverRating(0);
                  setReviewFormOpen((v) => {
                    const next = !v;
                    if (next && !myReview) {
                      setReviewDraft({ rating: 0, reviewText: '' });
                    }
                    return next;
                  });
                }}
                style={{
                  border: '1px solid #0b5226',
                  background: '#0b5226',
                  color: '#fff',
                  borderRadius: 10,
                  height: 38,
                  padding: '0 14px',
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                {'\u0417\u0430\u043B\u0438\u0448\u0438\u0442\u0438 \u0432\u0456\u0434\u0433\u0443\u043A'}
              </button>
            )}
          </div>

          {reviewFormOpen && user && !isBarista && (
            <form onSubmit={onSubmitReview} style={{ marginTop: 12, border: '1px solid #e5e7eb', borderRadius: 10, padding: 12 }}>
              <label style={{ display: 'grid', gap: 6, marginBottom: 10 }}>
                <span style={{ fontWeight: 700 }}>{'\u041E\u0446\u0456\u043D\u043A\u0430'}</span>
                <div
                  onMouseLeave={() => setReviewHoverRating(0)}
                  style={{ display: 'flex', gap: 4, alignItems: 'center' }}
                >
                  {[1, 2, 3, 4, 5].map((n) => {
                    const active = n <= (reviewHoverRating || Number(reviewDraft.rating) || 0);
                    return (
                      <button
                        key={n}
                        type="button"
                        onMouseEnter={() => setReviewHoverRating(n)}
                        onFocus={() => setReviewHoverRating(n)}
                        onBlur={() => setReviewHoverRating(0)}
                        onClick={() => setReviewDraft((prev) => ({ ...prev, rating: n }))}
                        aria-label={`\u041E\u0446\u0456\u043D\u043A\u0430 ${n}`}
                        style={{
                          border: 'none',
                          background: 'transparent',
                          padding: 0,
                          margin: 0,
                          fontSize: 26,
                          lineHeight: 1,
                          cursor: 'pointer',
                          color: active ? '#f59e0b' : '#d1d5db',
                        }}
                      >
                        {'\u2605'}
                      </button>
                    );
                  })}
                </div>
                {Number(reviewHoverRating || reviewDraft.rating || 0) > 0 && (
                  <div style={{ fontSize: 14, color: '#374151' }}>
                    {`${Number(reviewHoverRating || reviewDraft.rating || 0)} \u2B50 \u2014 \u201C${ratingLabelByValue[Number(reviewHoverRating || reviewDraft.rating || 0)] || ''}\u201D`}
                  </div>
                )}
              </label>
              {Number(reviewDraft.rating) > 0 && (
                <label style={{ display: 'grid', gap: 6 }}>
                  <span style={{ fontWeight: 700 }}>{'\u0412\u0456\u0434\u0433\u0443\u043A (\u043E\u043F\u0446\u0456\u043E\u043D\u0430\u043B\u044C\u043D\u043E)'}</span>
                  <textarea
                    value={reviewDraft.reviewText}
                    onChange={(e) => setReviewDraft((prev) => ({ ...prev, reviewText: e.target.value.slice(0, 2000) }))}
                    rows={5}
                    style={{ borderRadius: 8, border: '1px solid #d1d5db', padding: 10, resize: 'vertical' }}
                    placeholder={'\u0429\u043E \u0432\u0430\u043C \u0441\u043F\u043E\u0434\u043E\u0431\u0430\u043B\u043E\u0441\u044C \u0430\u0431\u043E \u043D\u0435 \u0441\u043F\u043E\u0434\u043E\u0431\u0430\u043B\u043E\u0441\u044C?'}
                  />
                  <span style={{ fontSize: 12, color: '#6b7280' }}>{`${(reviewDraft.reviewText || '').length}/2000`}</span>
                </label>
              )}
              <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                <button type="submit" disabled={reviewBusy || Number(reviewDraft.rating) < 1} style={{ border: '1px solid #0b5226', background: '#0b5226', color: '#fff', borderRadius: 10, height: 36, padding: '0 14px', fontWeight: 700, cursor: reviewBusy || Number(reviewDraft.rating) < 1 ? 'default' : 'pointer', opacity: reviewBusy || Number(reviewDraft.rating) < 1 ? 0.7 : 1 }}>
                  {reviewBusy ? '\u0417\u0431\u0435\u0440\u0435\u0436\u0435\u043D\u043D\u044F\u2026' : '\u0417\u0431\u0435\u0440\u0435\u0433\u0442\u0438'}
                </button>
                {myReview && (
                  <button
                    type="button"
                    onClick={onDeleteMyReview}
                    disabled={reviewBusy}
                    style={{
                      border: '1px solid #b91c1c',
                      background: '#fff',
                      color: '#b91c1c',
                      borderRadius: 10,
                      height: 36,
                      padding: '0 14px',
                      fontWeight: 700,
                      cursor: reviewBusy ? 'default' : 'pointer',
                      opacity: reviewBusy ? 0.7 : 1,
                    }}
                  >
                    Видалити відгук
                  </button>
                )}
                <button type="button" onClick={() => setReviewFormOpen(false)} style={{ border: '1px solid #d1d5db', background: '#fff', color: '#111827', borderRadius: 10, height: 36, padding: '0 14px', fontWeight: 600, cursor: 'pointer' }}>
                  {'\u0421\u043A\u0430\u0441\u0443\u0432\u0430\u0442\u0438'}
                </button>
              </div>
            </form>
          )}

          {reviewError && <div style={{ marginBottom: 10, color: '#991b1b', background: '#fee2e2', padding: '8px 10px', borderRadius: 8 }}>{reviewError}</div>}
          {reviewSuccess && <div style={{ marginBottom: 10, color: '#14532d', background: '#ecfdf3', padding: '8px 10px', borderRadius: 8 }}>{reviewSuccess}</div>}

          <div style={{ marginTop: 14 }}>
            {reviewsLoading ? (
              <div style={{ color: '#6b7280' }}>{'\u0417\u0430\u0432\u0430\u043D\u0442\u0430\u0436\u0435\u043D\u043D\u044F \u0432\u0456\u0434\u0433\u0443\u043A\u0456\u0432\u2026'}</div>
            ) : reviews.length === 0 ? (
              <div style={{ color: '#6b7280', whiteSpace: 'pre-line' }}>{'\u041F\u043E\u043A\u0438 \u0449\u043E \u043D\u0435\u043C\u0430\u0454 \u0432\u0456\u0434\u0433\u0443\u043A\u0456\u0432.\n\u0421\u0442\u0430\u043D\u044C\u0442\u0435 \u043F\u0435\u0440\u0448\u0438\u043C, \u0445\u0442\u043E \u043F\u043E\u0434\u0456\u043B\u0438\u0442\u044C\u0441\u044F \u0432\u0440\u0430\u0436\u0435\u043D\u043D\u044F\u043C'}</div>
            ) : (
              <div style={{ display: 'grid', gap: 10 }}>
              {reviews.map((review) => {
                const isHelpful = review.userVote === REVIEW_VOTE.HELPFUL;
                const isUnhelpful = review.userVote === REVIEW_VOTE.UNHELPFUL;
                const isOwnReview = Boolean(user?.uid) && review.userId === user.uid;
                return (
                  <article id={`review-${review.id}`} key={review.id} style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                        {(() => {
                          const displayName = review.userDisplayName || '\u041A\u043E\u0440\u0438\u0441\u0442\u0443\u0432\u0430\u0447';
                          const avatarUrl = String(review.userPhotoURL || '').trim();
                          const initials = getInitials(displayName);
                          return (
                            <>
                              <div style={{ width: 38, height: 38, borderRadius: '50%', overflow: 'hidden', border: '1px solid #d1d5db', background: '#f3f4f6', color: '#1f2937', display: 'grid', placeItems: 'center', fontWeight: 700, flex: '0 0 auto' }}>
                                {avatarUrl ? <img src={avatarUrl} alt={displayName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span>{initials}</span>}
                              </div>
                              <div>
                                <div style={{ fontWeight: 700 }}>{displayName}</div>
                                <div style={{ color: '#374151' }}>({review.rating}/5)</div>
                              </div>
                            </>
                          );
                        })()}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', justifyContent: 'flex-end', fontSize: 12, color: '#6b7280' }}>
                        {review.lastBookActionHint ? <span>{review.lastBookActionHint}</span> : null}
                      </div>
                    </div>

                    {review.reviewText ? (
                      <>
                        {(() => {
                          const isExpanded = Boolean(expandedReviewIds[review.id]);
                          const mayNeedExpand =
                            String(review.reviewText || '').split('\n').length > 5 ||
                            String(review.reviewText || '').length > 220;
                          return (
                            <>
                              <p
                                style={
                                  isExpanded
                                    ? { margin: '10px 0', whiteSpace: 'pre-wrap', color: '#111827' }
                                    : {
                                        margin: '10px 0',
                                        whiteSpace: 'pre-wrap',
                                        color: '#111827',
                                        display: '-webkit-box',
                                        WebkitLineClamp: 5,
                                        WebkitBoxOrient: 'vertical',
                                        overflow: 'hidden',
                                      }
                                }
                              >
                                {review.reviewText}
                              </p>
                              {mayNeedExpand && (
                                <button
                                  type="button"
                                  onClick={() => onToggleReviewExpanded(review.id)}
                                  style={{
                                    border: 'none',
                                    background: 'transparent',
                                    color: '#0b5226',
                                    fontWeight: 700,
                                    padding: 0,
                                    marginTop: -4,
                                    marginBottom: 8,
                                    cursor: 'pointer',
                                  }}
                                >
                                  {isExpanded ? 'Згорнути' : 'Показати більше'}
                                </button>
                              )}
                            </>
                          );
                        })()}
                      </>
                    ) : (
                      <p style={{ margin: '10px 0', color: '#111827', fontWeight: 700 }}>{renderStarIcons(review.rating)}</p>
                    )}

                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                      {isOwnReview && !isBarista && (
                        <button type="button" onClick={() => { setReviewHoverRating(0); setReviewFormOpen(true); }} style={{ border: '1px solid #d1d5db', background: '#f9fafb', color: '#111827', borderRadius: 999, padding: '4px 10px', cursor: 'pointer', fontWeight: 600 }}>
                          {'\u0420\u0435\u0434\u0430\u0433\u0443\u0432\u0430\u0442\u0438 \u0432\u0456\u0434\u0433\u0443\u043A'}
                        </button>
                      )}
                      <button type="button" onClick={() => onVoteReview(review.id, REVIEW_VOTE.HELPFUL)} style={{ border: `1px solid ${isHelpful ? '#0b5226' : '#d1d5db'}`, background: isHelpful ? '#ecfdf3' : '#fff', color: isHelpful ? '#16a34a' : '#6b7280', borderRadius: 999, padding: '4px 10px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, lineHeight: 1.2 }}>
                        <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" style={{ display: 'block' }}>
                          <path
                            d="M7 10v12M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2a3 3 0 0 1 3 3.88Z"
                            fill={isHelpful ? 'currentColor' : 'none'}
                            fillOpacity={isHelpful ? 0.25 : 1}
                            stroke="currentColor"
                            strokeWidth="1.9"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <span>({review.helpfulCount || 0})</span>
                      </button>
                      <button type="button" onClick={() => onVoteReview(review.id, REVIEW_VOTE.UNHELPFUL)} style={{ border: `1px solid ${isUnhelpful ? '#b91c1c' : '#d1d5db'}`, background: isUnhelpful ? '#fef2f2' : '#fff', color: isUnhelpful ? '#dc2626' : '#6b7280', borderRadius: 999, padding: '4px 10px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, lineHeight: 1.2 }}>
                        <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" style={{ display: 'block', transform: 'rotate(180deg)' }}>
                          <path
                            d="M7 10v12M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2a3 3 0 0 1 3 3.88Z"
                            fill={isUnhelpful ? 'currentColor' : 'none'}
                            fillOpacity={isUnhelpful ? 0.25 : 1}
                            stroke="currentColor"
                            strokeWidth="1.9"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <span>({review.unhelpfulCount || 0})</span>
                      </button>
                      <button type="button" onClick={() => onReportReview(review.id)} style={{ border: '1px solid #d1d5db', background: '#fff', color: '#374151', borderRadius: 999, padding: '4px 10px', cursor: 'pointer' }}>
                        {'\u041F\u043E\u0441\u043A\u0430\u0440\u0436\u0438\u0442\u0438\u0441\u044C'}
                      </button>
                      {isAdmin && (
                        <button type="button" onClick={() => onToggleHidden(review)} style={{ border: '1px solid #d1d5db', background: '#fff', color: '#111827', borderRadius: 999, padding: '4px 10px', cursor: 'pointer' }}>
                          {review.status === 'hidden' ? '\u041F\u043E\u043A\u0430\u0437\u0430\u0442\u0438' : '\u041F\u0440\u0438\u0445\u043E\u0432\u0430\u0442\u0438'}
                        </button>
                      )}
                      <span style={{ marginLeft: 'auto', fontSize: 12, color: '#6b7280' }}>
                        {review.createdAtMs ? new Date(review.createdAtMs).toLocaleDateString('uk-UA') : ''}
                      </span>
                    </div>
                  </article>
                );
              })}
              </div>
            )}
          </div>
        </div>
        {deleteReviewModalOpen && (
          <div
            onClick={() => { if (!reviewBusy) setDeleteReviewModalOpen(false); }}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(15, 23, 42, 0.45)',
              zIndex: 70,
              display: 'grid',
              placeItems: 'center',
              padding: 12,
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                width: 'min(460px, 100%)',
                background: '#fff',
                borderRadius: 12,
                border: '1px solid #e5e7eb',
                padding: 16,
              }}
            >
              <h3 style={{ margin: '0 0 8px 0' }}>Підтвердження видалення</h3>
              <div style={{ color: '#374151' }}>Видалити ваш відгук? Цю дію не можна скасувати.</div>
              <div style={{ marginTop: 14, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button
                  type="button"
                  onClick={() => setDeleteReviewModalOpen(false)}
                  disabled={reviewBusy}
                  style={{ border: '1px solid #d1d5db', background: '#fff', color: '#111827', borderRadius: 10, height: 36, padding: '0 14px', fontWeight: 600, cursor: reviewBusy ? 'default' : 'pointer' }}
                >
                  Скасувати
                </button>
                <button
                  type="button"
                  onClick={confirmDeleteMyReview}
                  disabled={reviewBusy}
                  style={{ border: '1px solid #b91c1c', background: '#b91c1c', color: '#fff', borderRadius: 10, height: 36, padding: '0 14px', fontWeight: 700, cursor: reviewBusy ? 'default' : 'pointer', opacity: reviewBusy ? 0.7 : 1 }}
                >
                  {reviewBusy ? 'Видалення…' : 'Видалити'}
                </button>
              </div>
            </div>
          </div>
        )}

        {reportModalOpen && (
          <div
            onClick={() => { if (!reportBusy) { setReportModalOpen(false); setReportTargetReviewId(null); setReportReason(''); } }}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(15, 23, 42, 0.45)',
              zIndex: 70,
              display: 'grid',
              placeItems: 'center',
              padding: 12,
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                width: 'min(520px, 100%)',
                background: '#fff',
                borderRadius: 12,
                border: '1px solid #e5e7eb',
                padding: 16,
              }}
            >
              <h3 style={{ margin: '0 0 8px 0' }}>Скарга на відгук</h3>
              <div style={{ color: '#374151', marginBottom: 10 }}>Вкажіть причину (необов'язково):</div>
              <textarea
                value={reportReason}
                onChange={(e) => setReportReason(e.target.value.slice(0, 500))}
                rows={4}
                style={{ width: '100%', borderRadius: 8, border: '1px solid #d1d5db', padding: 10, resize: 'vertical' }}
                placeholder="Опишіть проблему коротко"
              />
              <div style={{ marginTop: 6, fontSize: 12, color: '#6b7280' }}>{`${reportReason.length}/500`}</div>
              <div style={{ marginTop: 14, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button
                  type="button"
                  onClick={() => { setReportModalOpen(false); setReportTargetReviewId(null); setReportReason(''); }}
                  disabled={reportBusy}
                  style={{ border: '1px solid #d1d5db', background: '#fff', color: '#111827', borderRadius: 10, height: 36, padding: '0 14px', fontWeight: 600, cursor: reportBusy ? 'default' : 'pointer' }}
                >
                  Скасувати
                </button>
                <button
                  type="button"
                  onClick={submitReportReview}
                  disabled={reportBusy}
                  style={{ border: '1px solid #0b5226', background: '#0b5226', color: '#fff', borderRadius: 10, height: 36, padding: '0 14px', fontWeight: 700, cursor: reportBusy ? 'default' : 'pointer', opacity: reportBusy ? 0.7 : 1 }}
                >
                  {reportBusy ? 'Надсилання…' : 'Надіслати скаргу'}
                </button>
              </div>
            </div>
          </div>
        )}

        {reservationModalOpen && !isBarista && (
          <div
            onClick={() => setReservationModalOpen(false)}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(15, 23, 42, 0.45)',
              zIndex: 50,
              display: 'grid',
              placeItems: 'center',
              padding: 12,
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                width: 'min(680px, 100%)',
                maxHeight: '85vh',
                overflow: 'auto',
                background: '#fff',
                borderRadius: 12,
                border: '1px solid #e5e7eb',
                padding: 16,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
                <h3 style={{ margin: 0 }}>Оберіть кавʼярню</h3>
                <button
                  type="button"
                  onClick={() => setReservationModalOpen(false)}
                  style={{
                    border: '1px solid #d1d5db',
                    background: '#fff',
                    borderRadius: 8,
                    height: 32,
                    padding: '0 10px',
                    cursor: 'pointer',
                  }}
                >
                  Закрити
                </button>
              </div>

              {effectiveUserReservation && (
                <div style={{ marginBottom: 10, fontSize: 14, color: '#14532d', background: '#ecfdf3', borderRadius: 8, padding: '8px 10px' }}>
                  Ваша активна бронь до {formatUntil(Number(effectiveUserReservation.expiresAtMs))} ({formatRemaining(Number(effectiveUserReservation.expiresAtMs))}, {effectiveUserReservation.cafeName || 'кавʼярня'})
                </div>
              )}
              {reservationError && (
                <div style={{ marginBottom: 10, fontSize: 14, color: '#991b1b', background: '#fee2e2', borderRadius: 8, padding: '8px 10px' }}>
                  {reservationError}
                </div>
              )}

              <div style={{ display: 'grid', gap: 10 }}>
                {cafes.map((c) => {
                  const freeCount = freeCountsByCafe.get(c.name) ?? 0;
                  const cafeHasMyReservation =
                    effectiveUserReservation && (effectiveUserReservation.cafeName || '') === c.name;
                  return (
                    <div key={`res-cafe-${c.name}`} style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: 12 }}>
                      <div style={{ fontWeight: 700 }}>{c.name}</div>
                      <div style={{ fontSize: 13, color: '#6b7280' }}>📍 {c.address || 'Адреса відсутня'}</div>
                      <div style={{ marginTop: 6, fontSize: 14, color: freeCount > 0 ? '#166534' : '#6b7280' }}>
                        {freeCount > 0 ? `🟢 Вільно: ${freeCount}` : 'Немає вільних примірників'}
                      </div>

                      <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <button
                          type="button"
                          disabled={freeCount <= 0 || Boolean(effectiveUserReservation && !cafeHasMyReservation)}
                          onClick={() => onReserveAtCafe(c.name)}
                          style={{
                            border: '1px solid #0b5226',
                            background: freeCount > 0 ? '#0b5226' : '#9ca3af',
                            color: '#fff',
                            borderRadius: 9,
                            height: 34,
                            padding: '0 12px',
                            cursor: freeCount > 0 ? 'pointer' : 'not-allowed',
                            fontWeight: 700,
                          }}
                        >
                          {reservationBusyCafe === c.name ? 'Бронювання...' : 'Забронювати тут'}
                        </button>
                        {cafeHasMyReservation && (
                          <button
                            type="button"
                            onClick={onCancelMyReservation}
                            style={{
                              border: '1px solid #d1d5db',
                              background: '#fff',
                              color: '#111827',
                              borderRadius: 9,
                              height: 34,
                              padding: '0 12px',
                              cursor: 'pointer',
                              fontWeight: 600,
                            }}
                          >
                            Скасувати бронювання
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}











